import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Video } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useUpdateWherebySettings, useWherebyStatus } from '@/hooks/useWhereby';
import { ApiCallError } from '@/lib/api';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { StatusChip } from '@/components/ui/Chip';
import { IntegrationsBackLink } from '@/pages/settings/ClinicIntegrations';
import {
  SaveBar,
  SectionCard,
  SettingsLoading,
  SettingsSubpageHeader,
} from './settingsChrome';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export function ClinicWhereby() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const { data: status, isLoading, error } = useWherebyStatus(activeClinicId);
  const updateSettings = useUpdateWherebySettings(activeClinicId);

  const canAdmin = Boolean(
    activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId ?? '')),
  );

  const [enabled, setEnabled] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!status) return;
    setEnabled(status.enabled);
    setApiKey('');
    setClearApiKey(false);
    setDirty(false);
  }, [status]);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!activeClinicId) return;
    try {
      await updateSettings.mutateAsync({
        enabled,
        apiKey: apiKey.trim() || undefined,
        clearApiKey: clearApiKey || undefined,
      });
      toast.success(t('settings.integrations.whereby.saved'));
      setApiKey('');
      setClearApiKey(false);
      setDirty(false);
    } catch (err) {
      const message =
        err instanceof ApiCallError && err.message
          ? err.message
          : t('settings.integrations.whereby.save_error');
      toast.error(message);
    }
  };

  if (isLoading) {
    return <SettingsLoading />;
  }

  if (error && !status) {
    return <ErrorAlert message={t('settings.integrations.whereby.status_error')} />;
  }

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <IntegrationsBackLink />
      <SettingsSubpageHeader
        icon={<Video className="h-5 w-5" aria-hidden />}
        title={t('settings.integrations.cards.whereby_title')}
        subtitle={t('settings.integrations.whereby.subtitle')}
      />

      <SectionCard
        title={t('settings.integrations.whereby.status_title')}
        action={
          <StatusChip tone={status?.enabled ? 'completed' : 'neutral'}>
            {status?.enabled ? t('settings.integrations.whereby.status_on') : t('settings.integrations.whereby.status_off')}
          </StatusChip>
        }
      >
        <p className="text-ds-muted">{t('settings.integrations.whereby.status_desc')}</p>
        {status?.enabled && !status.configured ? (
          <Banner tone="warning" className="mt-s3">
            {t('settings.integrations.whereby.not_configured')}
          </Banner>
        ) : null}
      </SectionCard>

      <form onSubmit={(e) => void handleSave(e)} className="flex min-w-0 flex-col gap-s4">
        <SectionCard
          title={t('settings.integrations.whereby.enable_title')}
          subtitle={t('settings.integrations.whereby.enable_desc')}
        >
          <div className="flex items-center justify-between gap-s4">
            <div>
              <p className={cn(TYPE.section, 'text-ds-text')}>{t('settings.integrations.whereby.enable_toggle')}</p>
              <p className={cn(TYPE.small, 'text-ds-muted')}>{t('settings.integrations.whereby.enable_toggle_hint')}</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              disabled={!canAdmin}
              onClick={() => {
                setEnabled((value) => !value);
                setDirty(true);
              }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:shadow-ring disabled:cursor-not-allowed disabled:opacity-50 ${enabled ? 'bg-ds-teal' : 'bg-ds-switch-off'}`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-ds-knob shadow-card transition duration-200 ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
              />
            </button>
          </div>
        </SectionCard>

        {enabled ? (
          <SectionCard
            title={t('settings.integrations.whereby.key_title')}
            subtitle={t('settings.integrations.whereby.key_desc')}
          >
            <Input
              name="whereby-api-key"
              type="password"
              autoComplete="off"
              label={t('settings.integrations.whereby.key_label')}
              value={apiKey}
              onChange={(e) => {
                setApiKey(e.target.value);
                setClearApiKey(false);
                setDirty(true);
              }}
              placeholder={
                status?.hasClinicApiKey
                  ? t('settings.integrations.whereby.key_saved_placeholder')
                  : t('settings.integrations.whereby.key_placeholder')
              }
              hint={
                status?.hasClinicApiKey
                  ? t('settings.integrations.whereby.key_saved_hint')
                  : t('settings.integrations.whereby.key_hint')
              }
              disabled={!canAdmin}
            />
            {status?.hasClinicApiKey && canAdmin ? (
              <Button
                type="button"
                variant="secondary"
                className="mt-s3"
                onClick={() => {
                  setClearApiKey(true);
                  setApiKey('');
                  setDirty(true);
                }}
              >
                {t('settings.integrations.whereby.clear_key')}
              </Button>
            ) : null}
          </SectionCard>
        ) : null}

        {canAdmin && dirty ? <SaveBar canEdit pending={updateSettings.isPending} /> : null}
      </form>
    </div>
  );
}
